import { FileActionButton } from "../file-action/file-action-controls";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { createRoot } from "react-dom/client";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { Divider } from "primereact/divider";
import { DriverInfoDto } from "../../models/Delivery-Executive/DeliveryExecutive";
import { getDriversDetails } from "./driverApi";
import { generateProductSummaryPDF } from "../Driver-DeliveryDetails/DeliveryChallanPDF";
import orderServices from "../../services/orders/orderservice";
import {  ChallanProducts } from "../../models/Delivery-Executive/DeliveryExecutive";
import { Toast } from "primereact/toast";

const HOST_ID = "driver-picker-host";
const MAX_SELECT = 5; 

function ensureHost(): HTMLElement {
  let host = document.getElementById(HOST_ID);
  if (!host) {
    host = document.createElement("div");
    host.id = HOST_ID;
    document.body.appendChild(host);
  }
  return host;
}

type DriverPickerProps = {
  onClose: (picked: DriverInfoDto[] | null) => void;
  selectionCount?: number; 
  taxableAmount?: number; 
  orderIds?: (string | number)[];
};
export const DriverPicker: React.FC<DriverPickerProps> = ({ onClose, selectionCount = 0, taxableAmount = 0, orderIds = [], }) => {
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState<DriverInfoDto[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [vesselDetails, setVesselDetails] = useState<any[]>([]);
  const toastRef = useRef<Toast>(null);

  const initialOrderIdsRef = useRef(orderIds);
  useEffect(() => {
    if (initialOrderIdsRef.current?.length) {
      //console.log("DriverPicker → orderIds in boundary (initial):", initialOrderIdsRef.current);
    } else {
      console.log("DriverPicker → no orderIds passed.");
    }
  }, []);


  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await getDriversDetails();
        setDrivers(data ?? []);
        if (data?.length === 1) setSelectedIds([data[0].driverId]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const driversSorted = useMemo(
    () => [...drivers].sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base" })),
    [drivers]
  );

  const byId = useMemo(() => {
    const m = new Map<number, DriverInfoDto>();
    drivers.forEach((d) => m.set(d.driverId, d));
    return m;
  }, [drivers]);

  const confirmDisabled = selectedIds.length === 0;

  const inr = useMemo(
    () =>
      new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }),
    []
  );

  const handleExportPreChallan = async () => {
    try {
      const numericOrderIds = (initialOrderIdsRef.current || [])
        .map((id) => Number(id))
        .filter((id) => !isNaN(id));

      // call getPreChallanProducts with number[]
      const response = await orderServices.getPreChallanProducts(numericOrderIds);

      // prepare string orderIds for vessel
      const stringOrderIds = numericOrderIds.map((id) => id.toString());
      const vesselDetailsResponse = await orderServices.getVesselDetails(stringOrderIds);
      setVesselDetails(vesselDetailsResponse);

      const emptyState: ChallanProducts = {
        categorySummaries: [],
        lineItems: [],
        drivername: "",
        vesselDetails: []
      };

      if (!response) {
        await generateProductSummaryPDF(emptyState);
        return;
      }

      const isChallanProducts = (obj: any): obj is ChallanProducts => {
        return (
          (Array.isArray(obj.categorySummaries) || obj.categorySummaries === undefined) &&
          (Array.isArray(obj.lineItems) || obj.lineItems === undefined)
        );
      };

      const productsData: ChallanProducts = isChallanProducts(response)
        ? {
          categorySummaries: response.categorySummaries || [],
          lineItems: response.lineItems || [],
          drivername: response.drivername,
          vesselDetails: vesselDetailsResponse || []   // ✅ Pass vesselDetails here
        }
        : emptyState;

      if (productsData.categorySummaries.length === 0 && productsData.lineItems.length === 0) {
        return;
      }

      await generateProductSummaryPDF(productsData);
    } catch (error) {
      console.error("Error in handleExportPreChallan:", error);
      await generateProductSummaryPDF({
        categorySummaries: [],
        lineItems: [],
        drivername: "",
        vesselDetails: []
      });
    }
  };

  const Header = (
    <>
      <Toast ref={toastRef} />

      <div className="flex justify-content-between align-items-center w-full">
        <div className="flex align-items-center gap-2">
          <i className="pi pi-users text-primary" />
          <span className="font-semibold">Select Driver</span>

        </div>
        <div className="flex gap-2">
          <FileActionButton action="export"
            label="Export PreChallan"
            icon="pi pi-download"
            severity="info"
            onClick={handleExportPreChallan}
          />
          <Button
            label="Cancel"
            icon="pi pi-times"
            outlined
            severity="secondary"
            onClick={() => onClose(null)}
          />
          <Button
            label="Confirm"
            icon="pi pi-check"
            severity="success"
            disabled={confirmDisabled}
            onClick={async () => {
              const pickedDrivers = selectedIds.map((id) => byId.get(id)!).filter(Boolean);
              // Date-aware duplicate and fulfilment checks run atomically on save in the API.
              onClose(pickedDrivers);
            }}
          />
        </div>
      </div>
      <div className="ml-3 text-600 text-sm flex align-items-center gap-2" style={{ marginTop: 8 }}>
        <i className="pi pi-map-marker" />
        <strong>{selectionCount}</strong>&nbsp;
        order{selectionCount === 1 ? "" : "s"} selected in boundary
      </div>
      <div className="ml-3 text-700 text-sm flex align-items-center gap-2" style={{ marginTop: 4 }}>
        <i className="pi pi-wallet" />
        <span>
          Total  amount:&nbsp;<strong>{inr.format(taxableAmount || 0)}</strong>
        </span>
      </div>
    </>
  );


  return (
    <Dialog
      header={Header}
      visible
      modal
      closable={false}
      onHide={() => onClose(null)}
      style={{ width: "40rem", maxWidth: "95vw" }}
    >
      <div className="p-fluid">
        <div className="mb-3">
          <p className="text-600 text-sm mb-3">Choose one or more Delivery Executives to assign for route optimization</p>

          {loading ? (
            <div className="space-y-2">
              <Skeleton height="3rem" className="mb-2" />
              <div className="flex gap-2">
                <Skeleton height="1.5rem" width="30%" />
                <Skeleton height="1.5rem" width="20%" />
              </div>
            </div>
          ) : (
            <div className="field">
              <label htmlFor="driver-select" className="font-medium text-900 mb-2 block">
                Available Delivery Executives({drivers.length})
              </label>

              <MultiSelect
                inputId="driver-select"
                aria-label="Delivery executives"
                name="driver-select"
                options={driversSorted}
                optionLabel="name"                // shows name
                optionValue="driverId"            // use id as value
                value={selectedIds}               // number[]
                onChange={(e) => setSelectedIds(e.value as number[])}
                placeholder="Search and select driver(s)…"
                display="chip"
                selectionLimit={MAX_SELECT}       // allow up to two
                showSelectAll={false}
                maxSelectedLabels={MAX_SELECT}    // show up to 2 chips before collapsing
                selectedItemsLabel="{0} drivers selected"
                filter
                filterBy="name,phonenumber,posName,posCode"
                filterPlaceholder="Search drivers…"
                appendTo={document.body}
                emptyFilterMessage="No drivers found"
                panelHeaderTemplate={
                  <div className="px-3 py-2 border-bottom-1 surface-border">
                    <span className="font-semibold text-900">Select Delivery Executives</span>
                  </div>
                }
                itemTemplate={(d: DriverInfoDto) => (
                  <div className="flex align-items-center p-2 border-round">
                    <div className="flex-1">
                      <div className="flex align-items-center gap-2 mb-1">
                        <i className="pi pi-user text-primary text-sm" />
                        <span className="font-semibold text-900">{d.name}</span>
                        <span className="text-xs text-500">#{d.phonenumber}</span>
                        <span className="text-sm text-600">{d.posName || d.posCode || "POS unavailable"}</span>
                      </div>
                      {(d.startLocation || d.endLocation) && (
                        <div className="text-sm text-600 ml-4">
                          {d.startLocation && (
                            <div className="flex align-items-center gap-1 mb-1">
                              <i className="pi pi-map-marker text-green-500 text-xs" />
                              <span>Start: {d.startLocation}</span>
                            </div>
                          )}
                          {d.endLocation && (
                            <div className="flex align-items-center gap-1">
                              <i className="pi pi-flag text-red-500 text-xs" />
                              <span>End: {d.endLocation}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                selectedItemTemplate={(id: number) => (
                  <div className="flex align-items-center gap-2">
                    <i className="pi pi-user text-primary" />
                    <span className="font-medium">{byId.get(id)?.name ?? id}</span>
                  </div>
                )}
              />
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export function pickDrivers(ctx?: {
  selectionCount?: number;
  taxableAmount?: number;
  orderIds?: any[];
}): Promise<DriverInfoDto[] | null> {
  const host = ensureHost();
  const root = createRoot(host);

  return new Promise((resolve) => {
    const handleDriverClose = (picked: DriverInfoDto[] | null) => {
      if (!picked || picked.length === 0) {
        resolve(null);
        return root.unmount();
      }

      resolve(picked);
      setTimeout(() => root.unmount(), 0);
    };

    root.render(
      <DriverPicker
        selectionCount={ctx?.selectionCount ?? 0}
        taxableAmount={ctx?.taxableAmount ?? 0}
        orderIds={ctx?.orderIds ?? []}
        onClose={handleDriverClose}
      />
    );
  });
}
